import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useLanguage } from '../contexts/LanguageContext';

const FooterContainer = styled.footer`
  background-color: white;
  padding: 3rem 0 1.5rem;
  margin-top: auto;
  font-family: 'Montserrat', sans-serif;
  position: relative;
`;

const FooterBackground = styled.div`
  position: relative;
  width: 96%; /* Match hero section width */
  margin: 0 auto;
  background-color: #2d2d2d;
  border-radius: 8px;
  padding: 3rem 2rem 1.5rem;
  
  @media (max-width: 768px) {
    width: 94%; /* Match hero section mobile width */
  }
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const FooterColumns = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 2.5rem;
  margin-bottom: 3rem;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 2rem;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 2rem;
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const FooterColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const ColumnTitle = styled(Link)`
  color: white;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  margin-bottom: 1rem;
  border-bottom: 1px solid #444;
  padding-bottom: 0.5rem;
  text-decoration: none;
  cursor: pointer;
  transition: color 0.3s ease;
  
  &:hover {
    color: #ddd;
  }
`;

const FooterLink = styled(Link)`
  color: #ccc;
  text-decoration: none;
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
  transition: color 0.3s ease;
  cursor: pointer;
  
  &:hover {
    color: white;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 0;
  }
`;

const FooterExternalLink = styled.a`
  color: #ccc;
  text-decoration: none;
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
  transition: color 0.3s ease;
  
  &:hover {
    color: white;
  }
`;

const SocialIcons = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 0.25rem;
`;

const SocialIcon = styled.a`
  color: #ccc;
  font-size: 1.5rem;
  transition: color 0.3s ease;
  
  &:hover {
    color: white;
  }
  
  svg {
    width: 18px;
    height: 18px;
    fill: currentColor;
  }
`;

const FooterBottom = styled.div`
  border-top: 1px solid #444;
  padding-top: 1.5rem;
  display: flex;
  justify-content: center;
  align-items: baseline;
  gap: 1rem;
  flex-wrap: nowrap;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const CompanyInfo = styled.div`
  color: #ccc;
  font-size: 0.9rem;
  display: none;
`;

const LegalLinks = styled.div`
  display: flex;
  justify-content: center;
  align-items: baseline;
  gap: 1rem;
  flex-wrap: nowrap;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
  }
`;

const Copyright = styled.span`
  color: #999;
  font-size: 0.85rem;
  white-space: nowrap;
  line-height: 1;
  display: inline-block;
  
  @media (max-width: 768px) {
    white-space: normal;
    text-align: center;
  }
`;

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { language } = useLanguage();
  const navigate = useNavigate();
  
  const handleHashLinkClick = (e, path) => {
    e.preventDefault();
    const [route, hash] = path.split('#');
    
    // Navigate to the route
    navigate(route);
    
    // Scroll to the section after a short delay to ensure the page has loaded
    setTimeout(() => {
      if (hash) {
        const element = document.getElementById(hash);
        if (element) {
          // Scroll to position the element at the very top of the viewport
          const top = element.offsetTop;
          window.scrollTo({
            top: top,
            behavior: 'smooth'
          });
        }
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 100);
  };
  
  const footerText = {
    en: {
      home: 'HOME',
      latestPosts: 'Latest Posts',
      caseStudies: 'Case Studies',
      allArticles: 'All Articles',
      about: 'ABOUT',
      aboutUs: 'About Us',
      team: 'Team',
      contact: 'Contact',
      products: 'PRODUCTS',
      thinki: 'Thinki',
      downloads: 'Downloads',
      consulting: 'CONSULTING',
      services: 'Our Services',
      process: 'How We Work',
      experience: 'Experience',
      rates: 'Rates',
      community: 'COMMUNITY',
      newsletter: 'Newsletter',
      academy: 'Plix Academy',
      partnerships: 'Partnerships',
      companyInfo: 'PLIX AI GMBH • BERLIN',
      email: 'info@plix-ai.com',
      copyright: '© {year} Plix AI GmbH',
      impressum: 'Impressum',
      privacy: 'Privacy'
    },
    de: {
      home: 'HOME',
      latestPosts: 'Neueste Beiträge',
      caseStudies: 'Fallstudien',
      allArticles: 'Alle Artikel',
      about: 'ÜBER UNS',
      aboutUs: 'Über Uns',
      team: 'Team',
      contact: 'Kontakt',
      products: 'PRODUKTE',
      thinki: 'Thinki',
      downloads: 'Downloadbare Materialien',
      consulting: 'BERATUNG',
      services: 'Unser Angebot',
      process: 'Wie Wir Arbeiten',
      experience: 'Erfahrung',
      rates: 'Preise',
      community: 'COMMUNITY',
      newsletter: 'Newsletter',
      academy: 'Plix Academy',
      partnerships: 'Partnerschaften',
      companyInfo: 'PLIX AI GMBH • BERLIN',
      email: 'info@plix-ai.com',
      copyright: '© {year} Plix AI GmbH',
      impressum: 'Impressum',
      privacy: 'Datenschutz'
    }
  };
  
  const t = footerText[language];
  
  return (
    <FooterContainer>
      <FooterBackground>
        <FooterContent>
          <FooterColumns>
            {/* HOME Column */}
            <FooterColumn>
              <ColumnTitle to="/" onClick={(e) => handleHashLinkClick(e, "/")}>{t.home}</ColumnTitle>
              <FooterLink to="/#latestposts" onClick={(e) => handleHashLinkClick(e, "/#latestposts")}>{t.latestPosts}</FooterLink>
            </FooterColumn>
            
            {/* ABOUT Column */}
            <FooterColumn>
              <ColumnTitle to="/about" onClick={(e) => handleHashLinkClick(e, "/about")}>{t.about}</ColumnTitle>
              <FooterLink to="/about#aboutus" onClick={(e) => handleHashLinkClick(e, "/about#aboutus")}>{t.aboutUs}</FooterLink>
              <FooterLink to="/about#team" onClick={(e) => handleHashLinkClick(e, "/about#team")}>{t.team}</FooterLink>
              <FooterLink to="/about#contact" onClick={(e) => handleHashLinkClick(e, "/about#contact")}>{t.contact}</FooterLink>
            </FooterColumn>
            
            {/* CONSULTING Column */}
            <FooterColumn>
              <ColumnTitle to="/consulting" onClick={(e) => handleHashLinkClick(e, "/consulting")}>{t.consulting}</ColumnTitle>
              <FooterLink to="/consulting#services" onClick={(e) => handleHashLinkClick(e, "/consulting#services")}>{t.services}</FooterLink>
              <FooterLink to="/consulting#process" onClick={(e) => handleHashLinkClick(e, "/consulting#process")}>{t.process}</FooterLink>
              <FooterLink to="/consulting#experience" onClick={(e) => handleHashLinkClick(e, "/consulting#experience")}>{t.experience}</FooterLink>
              {/* Rates link hidden for now – do not delete
              <FooterLink to="/consulting#rates" onClick={(e) => handleHashLinkClick(e, "/consulting#rates")}>{t.rates}</FooterLink>
              */}
            </FooterColumn>
            
            {/* PRODUCTS Column */}
            <FooterColumn>
              <ColumnTitle to="/products" onClick={(e) => handleHashLinkClick(e, "/products")}>{t.products}</ColumnTitle>
              <FooterLink to="/products#downloads" onClick={(e) => handleHashLinkClick(e, "/products#downloads")}>{t.downloads}</FooterLink>
              {/* Thinki link hidden for now – do not delete
              <FooterLink to="/products#thinki" onClick={(e) => handleHashLinkClick(e, "/products#thinki")}>{t.thinki}</FooterLink>
              */}
            </FooterColumn>
            
            {/* COMMUNITY Column */}
            <FooterColumn>
              <ColumnTitle to="/partnerships" onClick={(e) => handleHashLinkClick(e, "/partnerships")}>{t.community}</ColumnTitle>
              <FooterLink to="/partnerships#newsletter" onClick={(e) => handleHashLinkClick(e, "/partnerships#newsletter")}>{t.newsletter}</FooterLink>
              {/* Plix Academy link hidden for now – do not delete
              <FooterLink to="/partnerships#academy" onClick={(e) => handleHashLinkClick(e, "/partnerships#academy")}>{t.academy}</FooterLink>
              */}
              <FooterLink to="/partnerships#partnerships" onClick={(e) => handleHashLinkClick(e, "/partnerships#partnerships")}>{t.partnerships}</FooterLink>
              <SocialIcons>
                <SocialIcon href="mailto:info@plix-ai.com" aria-label="Email">
                  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                  </svg>
                </SocialIcon>
                <SocialIcon href="https://github.com/plix-ai/" target="_blank" rel="noopener noreferrer" aria-label="GitHub">
                  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                  </svg>
                </SocialIcon>
                <SocialIcon href="https://www.linkedin.com/company/plix-ai" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </SocialIcon>
              </SocialIcons>
            </FooterColumn>
          </FooterColumns>
          
          <FooterBottom>
            <CompanyInfo>
              {t.companyInfo} • <FooterExternalLink href={`mailto:${t.email}`}>{t.email}</FooterExternalLink>
            </CompanyInfo>
            
            <LegalLinks>
              <Copyright>{t.copyright.replace('{year}', currentYear)}</Copyright>
              <FooterLink to="/legal#impressum" onClick={(e) => handleHashLinkClick(e, "/legal#impressum")}>{t.impressum}</FooterLink>
              <FooterLink to="/legal#privacy" onClick={(e) => handleHashLinkClick(e, "/legal#privacy")}>{t.privacy}</FooterLink>
            </LegalLinks>
          </FooterBottom>
        </FooterContent>
      </FooterBackground>
    </FooterContainer>
  );
};

export default Footer; 